import parsePhoneNumberFromString, {
    CountryCode,
    getCountries,
    getCountryCallingCode,
    isValidPhoneNumber,
} from 'libphonenumber-js';

export type PhoneCountryInfo = Readonly<{
    countryCode: CountryCode;
    callingCode: string;
}>;

/**
 * List of phone country info.
 */
export const phoneCountryInfo: PhoneCountryInfo[] = getCountries().map((countryCode) => ({
    countryCode,
    callingCode: `${getCountryCallingCode(countryCode)}`,
}));

const infoLookup = new Map(phoneCountryInfo.map((info) => [info.countryCode, info]));

/**
 * Removes any other values from a string that are not a digit
 * @param phoneNumber the phone number being provided
 * @returns the digits of the phone number
 */
export const toRawPhoneNumber = (phoneNumber: string) =>
    phoneNumber.replace(/[^0-9]/g, '');

/**
 * Get the country info from the country code
 * @param countryCode the country code for the country being searched for
 * @returns the PhoneCountryInfo for the country code or undefined
 */
export const getCountryInfoFromCode = (
    countryCode: string
): PhoneCountryInfo | undefined =>
    infoLookup.get(countryCode.toUpperCase() as CountryCode);

/**
 * Get the country info from the phone number
 * @param phoneNumber raw phone number with + at the start of the string
 * @returns the PhoneCountryInfo for the phone number or undefined
 */
export const getCountryInfoFromNumber = (
    phoneNumber: string
): PhoneCountryInfo | undefined => {
    const parsed = parsePhoneNumberFromString(phoneNumber);

    return parsed?.country !== undefined
        ? {
              countryCode: parsed.country,
              callingCode: `${getCountryCallingCode(parsed.country)}`,
          }
        : undefined;
};

/**
 * Determines whether phone number will be formatted according to national/international standards
 */
export type PhoneNumberFormatFormat = 'national' | 'international';

/**
 * Formats phone number to national/international standard
 * @param phoneNumber The phone number being provided (number must be in raw phone number format with the + symbol at the front)
 * @param format the format type of the phone number
 * @returns the number formatted to national/international standard
 */
export const formatPhoneNumber = (
    phoneNumber: string | undefined,
    format: PhoneNumberFormatFormat = 'international'
) => {
    const parsed = phoneNumber ? parsePhoneNumberFromString(phoneNumber) : undefined;

    if (!parsed) {
        return phoneNumber ?? '';
    }
    if (format === 'national') {
        return parsed.formatNational();
    }
    return parsed.formatInternational();
};

/**
 * Determines whether or not provided number is valid
 * @param phoneNumber raw phone number with + at the start of the string
 * @param countryCode country code of the phone number
 * @returns true if number is valid
 */
export const isPhoneNumberValid = (
    phoneNumber: string,
    countryCode?: CountryCode
): boolean => isValidPhoneNumber(phoneNumber, countryCode);
